// Pieces the MPS board and the workbench share (spec 03 §4.1, §4.4): the plan
// status chip, the self-approval mark and the workbench's MPS tile.

import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { StatTile } from "../../../../components/stat-strip";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import { formatTimestamp } from "../../../../lib/datetime";
import { useCurrentMpsPlan, type MpsPlanStatus, type MpsPlanSummary } from "../hooks/mps-queries";

export const MPS_STATUS_VARIANT: Readonly<Record<MpsPlanStatus, ChipVariant>> = {
  proposed: "accent",
  adjusting: "accent",
  pending_approval: "warn",
  approved: "ok",
  superseded: "neutral",
};

export function MpsStatusChip({ status, testId }: { status: MpsPlanStatus; testId?: string }) {
  const { t } = useTranslation();
  return (
    <StatusChip
      variant={MPS_STATUS_VARIANT[status]}
      data-status={status}
      data-testid={testId ?? "planning-mps-status"}
    >
      {t(`planning.mps.status.${status}`)}
    </StatusChip>
  );
}

/** "Approved automatically on submit", beside the chip of a plan that approved itself (§2.7). */
export function MpsAutoMark({ plan }: { plan: Pick<MpsPlanSummary, "auto_approved"> }) {
  const { t } = useTranslation();
  if (!plan.auto_approved) return null;
  return (
    <StatusChip variant="neutral" dot={false} data-testid="planning-mps-auto">
      {t("planning.mps.autoApproved")}
    </StatusChip>
  );
}

/** §4.1's MPS status tile: the open or approved plan, and whether demand moved since. */
export function MpsTile({
  facilityId,
  timezone,
}: {
  facilityId: string;
  timezone: string | null | undefined;
}) {
  const { t, i18n } = useTranslation();
  const current = useCurrentMpsPlan(facilityId);
  const plan = current.data;
  return (
    <StatTile
      label={t("planning.workbench.tiles.mps")}
      loading={current.isPending}
      data-testid="planning-tile-mps"
      value={
        plan === undefined || plan === null ? (
          "–"
        ) : (
          <Link to="/planning/mps" className="planning-mps-tile-link" data-plan-id={plan.id}>
            {plan.plan_no}
          </Link>
        )
      }
      sub={
        current.error !== null ? (
          t("planning.workbench.tiles.mpsError")
        ) : plan === undefined ? undefined : plan === null ? (
          t("planning.workbench.tiles.mpsNone")
        ) : (
          <span className="planning-mps-tile-sub">
            <MpsStatusChip status={plan.status} testId="planning-tile-mps-status" />
            <MpsAutoMark plan={plan} />
            <span>
              {t("planning.workbench.tiles.mpsProposed", {
                time: formatTimestamp(plan.proposed_at, i18n.language, timezone),
              })}
            </span>
            {plan.demand_changed_since_proposal ? (
              <span className="planning-mps-tile-changed" data-testid="planning-tile-mps-changed">
                {t("planning.mps.demandChanged")}
              </span>
            ) : null}
          </span>
        )
      }
    />
  );
}
